import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../store';
import { deleteEntity, getEntities, reset } from '../../../manager/sys-perfil.reducer';

const BASE = '/configuracion/seguridad/perfiles-sistema';

const fmtDate = (v: unknown) => {
  if (v == null || v === '') return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
};

export default function PerfilesSistemaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.sysPerfil.entities);
  const totalItems = useAppSelector(s => s.sysPerfil.totalItems);
  const loading = useAppSelector(s => s.sysPerfil.loading);
  const updating = useAppSelector(s => s.sysPerfil.updating);
  const updateSuccess = useAppSelector(s => s.sysPerfil.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Perfil de sistema eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton
        onClick={() => {
          ask(
            <>
              ¿Confirmas que deseas eliminar el perfil <strong>{params.row.nombre}</strong>? Esta acción no se puede
              deshacer.
            </>,
            () => dispatch(deleteEntity(params.row.id)),
            { title: 'Eliminar perfil de sistema', confirmLabel: 'Eliminar', variant: 'danger' },
          );
          topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    { field: 'nombre', headerName: 'Nombre', flex: 1.5 },
    {
      field: 'created',
      headerName: 'Creado',
      flex: 1.2,
      renderCell: (p: any) => fmtDate(p.value),
    },
    { field: 'createdBy', headerName: 'Creado por', flex: 0.8 },
    {
      field: 'updated',
      headerName: 'Actualizado',
      flex: 1.2,
      renderCell: (p: any) => fmtDate(p.value),
    },
    { field: 'updatedBy', headerName: 'Actualizado por', flex: 0.9 },
    { field: '__actions', headerName: '', flex: 0.85, renderCell: renderActions },
  ];

  const filters = [
    { label: 'ID', name: 'id' },
    { label: 'Nombre', name: 'nombre' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
          { label: 'Seguridad', to: '/configuracion/seguridad', icon: <FaShieldAlt /> },
          { label: 'Perfiles de Sistema', icon: <FaShieldAlt /> },
        ]}
      />

      <PageHeader
        icon={<FaShieldAlt />}
        iconColor="#6366f1"
        title="Perfiles de Sistema"
        subtitle="Roles y perfiles configurados en el sistema"
        action={{
          label: (
            <>
              <FaPlus style={{ marginRight: 6 }} />
              Nuevo Perfil de Sistema
            </>
          ),
          onClick: () => navigate(`${BASE}/new`),
          color: '#6366f1',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
